import outgoing from "@/assets/Images/icons/calltype/call-outgoing.svg";
import { ref } from "vue";
// import unanswered from '@/assets/Images/icons/calltype/unanswered.svg'
import {
  BusinessCallLogOutSchema,
  PagedBusinessCallLogOutSchema,
} from "@/api/client";
import { handleError } from "@/api/handleError";
import incoming from "@/assets/Images/icons/calltype/call-incoming.svg";
import missed from "@/assets/Images/icons/calltype/call-missed.svg";
import {
  default as internal,
  default as voicemailcall,
} from "@/assets/Images/icons/calltype/call-outgoing.svg";
import transferred from "@/assets/Images/icons/calltype/call-transferred.svg";
import { useDialer } from "@/modules/dialer/composables/useDialer";
import { useBusinessStore } from "@/stores/businesses.store";
import { useCallStore } from "@/stores/call.store";
import { useNumberStore } from "@/stores/numbers.store";
import { useUsersStore } from "@/stores/users.store";
import { useUtilsStore } from "@/stores/utils.store";
import { notify } from "@kyvg/vue3-notification";
import dayjs from "dayjs";
import { defaultContactState, predefinedGradients } from "./constants";
import { useContactCallQueue } from "./contact-call-queue";
import {
  exportCallHistory,
  fetchCallLogDetailsById,
  fetchIndividualCallLength,
  filterCallHistoryNew,
  getCallHistoryNew,
} from "./queries";
import { CallInfo, CallTypeFormatting, SortedData } from "./types/call";
import {
  formatNigerianNumber,
  formatPhoneNumberWithCountryCodeAndCountry,
  isNigerianNumber,
} from "./utils";

const callList = ref<PagedBusinessCallLogOutSchema | null>(null);
const isLoading = ref(false);

export function useCallLogs() {
  const callStore = useCallStore();
  const dialer = useDialer();
  const numberStore = useNumberStore();
  const utilsStore = useUtilsStore();
  const businessStore = useBusinessStore();
  const { handleFetchContact } = useContactCallQueue();

  const callList = ref<PagedBusinessCallLogOutSchema | null>(null);
  const avatarGradient = ref<string>(
    "linear-gradient(180deg, #FF8268 0%, rgba(59, 45, 42, 0.76) 100%)"
  );

  const callContact = async (
    number?: string,
    skipNumberValidation?: boolean
  ) => {
    if (!number) {
      notify({
        text: "Please enter a valid phone number",
        type: "error",
      });
      return;
    }

    if (utilsStore.showDisconnectionPrompt) {
      notify({
        text: "Your line is not connected. Account restricted due to outstanding payment",
        type: "error",
      });
      return;
    }

    if (!numberStore?.currentUserReceiverKey) {
      notify({
        text: "Your line is not connected. Please check your internet connection and try again",
        type: "error",
      });
      return;
    }

    // Check connection using dialer composable
    // Check if any instance is connected
    const anyConnected = Object.values(dialer.dialer.sipConnectionState).some(
      (status) => status.isConnected
    );
    if (!anyConnected) {
      notify({
        text: "Your line is not connected. Please check your internet connection and try again",
        type: "error",
      });
      return;
    }

    if (!skipNumberValidation) {
      if (number.length < 9) {
        notify({
          text: "Please enter a valid phone number",
          type: "error",
        });
        return;
      }
      if (
        number.startsWith("+234") &&
        numberStore.activeNumber?.business_number?.country_code.toLowerCase() ===
          "ng"
      ) {
        number = "0" + number.slice(4);
      } else if (
        number.startsWith("234") &&
        numberStore.activeNumber?.business_number?.country_code.toLowerCase() ===
          "ng"
      ) {
        number = "0" + number.slice(3);
      }

      if (
        // if pressone users are using international numbers to call it must be in country code format
        numberStore.activeNumber?.business_number?.country_code.toLowerCase() !==
        "ng"
      ) {
        if (!isNigerianNumber(number) && !number.startsWith("+")) {
          notify({
            text: "Please ensure you add (+) with your country code before dialing.",
            type: "error",
          });
          return;
        } else {
          number = formatNigerianNumber(number);
        }
      }
    }

    dialer.dialer.hideOutboundModal();

    try {
      // Use the dialer composable's call method
      await dialer.call({ number });
    } catch (error) {
      handleError({ error });
    }
  };

  const cleanInput = () => {
    dialer.dialer.clearNumberToCall();
  };

  const getCallHistory = async (
    businessId?: number,
    source?: string,
    skipRowSelection?: boolean
  ) => {
    const userStore = useUsersStore();
    try {
      isLoading.value = true;

      const getAllLogs = await listOfCallHistory(
        !userStore.canAccessManagerView ? "10" : "25",
        "",
        undefined,
        true,
        businessId,
        source
      );

      callStore.callList = getAllLogs;
      callStore.totalLogs = callStore.callList?.total as number;
      const callLogs = callStore.callList?.data || [];
      callStore.pageIndex = callStore.callList?.page_index;
      if (!skipRowSelection) {
        void selectRow(callLogs[0], 0); // fetch call details of either the first or index passed
      }
      isLoading.value = false;
    } catch (error) {
      handleError({ error });
    } finally {
      callStore.fetchFilter = false;
      isLoading.value = false;
    }
  };

  const getFilteredHistory = async (sortedData: SortedData) => {
    isLoading.value = true;
    try {
      const getFilteredLogs = await listOfFilteredCallHistory(sortedData);
      callStore.callList = getFilteredLogs;
      if (getFilteredLogs) {
        callStore.totalLogs = getFilteredLogs.total;
        const callLogs = callStore.callList?.data;
        callStore.pageIndex = callStore.callList?.page_index;
        if (callLogs && callLogs.length > 0) {
          void selectRow(callLogs[0], 0); // fetch call details of either the first or index passed
        } else {
          void selectRow(undefined, 0);
        }
      }
      isLoading.value = false;
    } catch (error) {
      handleError({ error });
      isLoading.value = false;
    } finally {
      isLoading.value = false;
    }
  };

  const selectRow = async (
    selectedRow?: BusinessCallLogOutSchema,
    index?: number,
    gradient?: string
  ) => {
    if (gradient) {
      avatarGradient.value = gradient;
    }

    try {
      // allow call note to unmount and remount to prevent caching
      callStore.showCallNotesCard = false;

      callStore.isFetchingDetails = true;

      // if details is fetched from logs update with available details. This could be called from activities stream also
      if (selectedRow) {
        callStore.selectedCallInfo = {
          ...(callStore.selectedCallInfo || {}),
          ...selectedRow,
        } as CallInfo;
        const userId = callStore.selectedCallInfo?.user
          ? callStore.selectedCallInfo.user
          : undefined;
        callStore.selectedTeamMemberLog = userId ? userId : null;
      }

      if (callStore.selectedCallInfo?.contact) {
        const contact = await handleFetchContact(
          callStore.selectedCallInfo.contact
        );
        callStore.selectedCallContactDetails = contact || null;
      } else {
        // if no contact is available, set selectedCallContactDetails to default mutable state
        callStore.selectedCallContactDetails = defaultContactState();
      }

      callStore.successSave = false;

      if (index) {
        callStore.selectedIndex = index;
      }

      if (selectedRow && selectedRow.id) {
        const response = await fetchCallLogDetailsById(String(selectedRow?.id));

        callStore.selectedCallInfo = response.data;
        callStore.showCallNotesCard = true; // allow call note to unmount and remount to prevent caching
      }
    } catch (error) {
      handleError({ error });
    } finally {
      callStore.isFetchingDetails = false;
    }
  };

  const navigate = async (link: string, index?: number) => {
    if (link) {
      isLoading.value = true;
      try {
        const getAllLogs = await listOfCallHistory("", link);

        callStore.callList = getAllLogs;
        if (getAllLogs && getAllLogs.total) {
          callStore.totalLogs = getAllLogs.total;
        }

        // Check if results exist before accessing the first item
        if (callStore.callList?.data && callStore.callList?.data.length > 0) {
          const callLogs = callStore.callList?.data;
          callStore.pageIndex = callStore.callList?.page_index;
          void selectRow(callLogs[index ? index : 0], index ? index : 0); // fetch call details of either the first or index passed
        }

        if (callStore.selectedCallInfo?.contact) {
          const contact = await handleFetchContact(
            callStore.selectedCallInfo.contact
          );
          callStore.selectedCallContactDetails = contact || null;
        } else {
          // if no contact is available, set selectedCallContactDetails to default mutable state
          callStore.selectedCallContactDetails = defaultContactState();
        }
        isLoading.value = false;
        callStore.successSave = false;
      } catch (error) {
        handleError({ error });
      } finally {
        isLoading.value = false;
      }
    } else {
      return notify({
        text: "No more records available on the next page.",
        type: "error",
      });
    }
  };

  const numberInputRules = [
    (v: string) => !!v || "Phone number is required",
    (v: string) => {
      const strippedNumber = v.replace(/\s/g, ""); // Remove spaces from the input
      return (
        /^\+?\d+$/.test(strippedNumber) ||
        "Invalid Phonenumber. Input must be a valid number (allowing +)"
      );
    },
  ];

  const displayVerificationModal = () => {
    if (
      utilsStore.hasMadeACall &&
      !businessStore.activeProfile?.business?.verified
    ) {
      utilsStore.showVerifyModalForCall = true;
      return;
    }
    dialer.dialer.showOutboundModal();
  };

  const callTeamMember = (ext: string) => {
    // Use the dialer composable's call method to call team members directly
    void dialer.call({ number: ext });
  };

  const generateNewNoteObject = () => {
    // create a new call note object if no notes exist
    return {
      note: "",
      call_id: callStore?.selectedCallInfo?.sip_call_id,
      contact_number: callStore?.selectedCallInfo?.is_dialed
        ? formatPhoneNumberWithCountryCodeAndCountry(
            callStore?.selectedCallInfo?.recipient_number || ""
          ).formattedPhoneNumber
        : formatPhoneNumberWithCountryCodeAndCountry(
            callStore?.selectedCallInfo?.caller_id || ""
          ).formattedPhoneNumber,
      business_number_id: numberStore?.activeNumber?.business_number?.id,
      call_log_id: callStore?.selectedCallInfo?.id,
      date_created: new Date().toISOString(),
    };
  };

  const getGradient = (index: number) => {
    const gradientIndex =
      index < predefinedGradients.length ? index : index % 2;
    return predefinedGradients[gradientIndex];
  };

  return {
    getFilteredHistory,
    getCallHistory,
    isLoading,
    callList,
    selectRow,
    avatarGradient,
    navigate,
    callContact,
    cleanInput,
    numberInputRules,
    displayVerificationModal,
    callTeamMember,
    generateNewNoteObject,
    getGradient,
  };
}
export const listOfCallHistory = async (
  pageSize: string,
  link: string,
  agent_id?: number,
  showAllLogs?: boolean,
  businessId?: number,
  source?: string
) => {
  try {
    const requestLink = link ? link : "";
    const callListData = await getCallHistoryNew(
      pageSize,
      requestLink,
      agent_id,
      showAllLogs,
      businessId,
      source
    );
    callList.value = callListData.data;
    return callList.value;
  } catch (error) {
    handleError({ error });
    return null;
  }
};
export const listOfFilteredCallHistory = async (filter: SortedData) => {
  try {
    const response = await filterCallHistoryNew(filter);
    callList.value = response.data;
    return callList.value;
  } catch (error) {
    handleError({ error });
    return null;
  }
};

export const exportCallLogs = async () => {
  try {
    await exportCallHistory();
  } catch (error) {
    handleError({ error });
  }
};
export function determineCallStatus( // get call type and icon
  is_dialed?: boolean,
  is_forwarded_call?: boolean,
  is_missed_call?: boolean,
  has_voice_mail?: boolean,
  recording?: string | null
): CallTypeFormatting {
  let displayType: CallTypeFormatting = {
    icon: "",
    color: "",
    type: "",
    textColor: "",
    fontColor: "",
  };
  if (is_dialed) {
    displayType = {
      ...displayType,
      icon: outgoing,
      color: "#ebfff0",
      type: "Outgoing",
      textColor: "#E3EFFC",
      fontColor: "#34C759",
    };
  } else if (!is_dialed && !!recording && !is_missed_call) {
    if (!is_forwarded_call)
      displayType = {
        ...displayType,
        icon: incoming,
        color: "#E3EFFC",
        type: "Incoming",
        textColor: "#E3EFFC",
        fontColor: "#3D89DF",
      };
    else
      displayType = {
        ...displayType,
        icon: transferred,
        color: "#ebfff0",
        type: "Forwarded",
        textColor: "#E3EFFC",
      };
  } else if (!is_dialed && is_missed_call) {
    displayType = {
      ...displayType,
      icon: missed,
      color: "#FBEAE9",
      type: "Missed",
      textColor: "#E3EFFC",
      fontColor: "#FF3B30",
    };
  } else if (has_voice_mail) {
    displayType = {
      ...displayType,
      icon: voicemailcall,
      color: "#ebfff0",
      type: "Voicemail",
      textColor: "#E3EFFC",
    };
  } else {
    displayType = {
      ...displayType,
      icon: internal,
      color: "#ebfff0",
      type: "Internal",
      textColor: "#E3EFFC",
    };
  }
  return displayType;
}

const getCallTypeFormatting = (
  callLog: BusinessCallLogOutSchema
): CallTypeFormatting => {
  return determineCallStatus(
    callLog?.is_dialed,
    callLog?.is_forwarded_call,
    callLog?.is_missed_call,
    callLog?.has_voice_mail,
    callLog?.recording_url
  );
};
export const getCallStatusType = (
  callLog?: BusinessCallLogOutSchema | null
): string => {
  const { type } = getCallTypeFormatting(callLog!);
  return type;
};

export const getCallStatusIcon = (
  callLog?: BusinessCallLogOutSchema | null
): string => {
  const { icon } = getCallTypeFormatting(callLog!);

  return icon;
};

export const getCallStatusBackgroundColor = (callLog: CallInfo): string => {
  const { color } = getCallTypeFormatting(callLog);
  return color;
};

export const getCallStatusTextColor = (callLog: CallInfo): string => {
  const { color } = getCallTypeFormatting(callLog);
  return color;
};

export const onNextPage = async (response: PagedBusinessCallLogOutSchema) => {
  try {
    if (response.next) {
      await listOfCallHistory("", response.next);
    }
  } catch (error) {
    handleError({ error });
  }
};

export const onPrevPage = async (response: PagedBusinessCallLogOutSchema) => {
  try {
    if (response.previous) {
      await listOfCallHistory("", response.previous);
    }
  } catch (error) {
    handleError({ error });
  }
};
export const getMaxLimit = (value: number) => {
  let maxLimit = 0;
  switch (true) {
    case value >= 100 && value < 200:
      maxLimit = 100;
      break;
    case value >= 50 && value < 100:
      maxLimit = 50;
      break;
    case value >= 25 && value < 50:
      maxLimit = 25;
      break;
    default:
      maxLimit = 10;
  }
  return maxLimit;
};

export function getDateRange(period: string) {
  const today = dayjs();
  let fromDate;
  switch (period) {
    case "past_week":
      fromDate = today.subtract(6, "day").format("YYYY-MM-DD");
      break;
    case "past_month":
      fromDate = today
        .subtract(1, "month")
        .startOf("month")
        .format("YYYY-MM-DD");
      break;
    case "past_year":
      fromDate = today.subtract(1, "year").startOf("year").format("YYYY-MM-DD");
      break;
    default:
      throw new Error("Invalid period provided");
  }

  const toDate = today.format("YYYY-MM-DD");
  return { from_date: fromDate, to_date: toDate };
}
export const fetchCallLogsForAgents = async () => {
  const utilsStore = useUtilsStore();

  try {
    const response = await fetchIndividualCallLength();
    if (response?.data?.total) {
      utilsStore.totalCalls = response?.data?.total;
    }
  } catch (error) {
    handleError({ error, fallbackMessage: "Error fetching call logs" });
  }
};
